import { useState } from 'react';
import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading2,
  Heading5,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react';
import { PricingDTOTypes } from '@repo/experience-api-client';
import {
  formatSteppedDiscountCopyFragment,
  hasThreeTierDiscountSteps,
} from '../../utils/discountSteps';

interface FaqItem {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
  readonly initialIsExpanded?: boolean;
}

function buildFaqs(discountSteps: PricingDTOTypes.DiscountStepDTO[]): readonly FaqItem[] {
  const discountFaq: FaqItem[] = hasThreeTierDiscountSteps(discountSteps)
    ? [
        {
          id: 'faqDiscount',
          question: 'How does the Gousto discount work?',
          answer: `With our generous new customer discount offer, you can get ${formatSteppedDiscountCopyFragment(discountSteps)}.`,
          initialIsExpanded: true,
        },
      ]
    : [];

  return [
    ...discountFaq,
    {
      id: 'faqIncluded',
      question: "What's included in a Gousto box?",
      answer:
        'Each Gousto box contains pre-portioned ingredients and easy-to-follow recipe cards for the meals you choose. All produce is fresh, and packaging is kept to a minimum.',
      initialIsExpanded: !hasThreeTierDiscountSteps(discountSteps),
    },
    {
      id: 'faqCancel',
      question: 'Can I skip or cancel my subscription?',
      answer:
        'Yes — there is no lock-in. You can skip a week or cancel your subscription at any time, completely free of charge.',
    },
  ];
}

const FaqItemDivider = withCitrusTheme(Box, (theme) => ({
  borderBottom: `1px solid ${theme.color(Color.ColdGrey_100)}`,
}));

const FaqRowButton = withCitrusTheme('button', (theme) => ({
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  width: '100%',
  padding: '8px',
  textAlign: 'left',
  '@media (min-width: 768px)': {
    padding: '16px 6px 17px 6px',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.color(Color.Secondary_600)}`,
    outlineOffset: '2px',
  },
}));

interface FaqRowProps {
  readonly faq: FaqItem;
}

function FaqRow({ faq }: FaqRowProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState<boolean>(faq.initialIsExpanded ?? false);
  const contentId = `${faq.id}-content`;

  return (
    <FaqItemDivider>
      <FaqRowButton
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <Box
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.SpaceBetween}
          gap={2}
        >
          <Heading5 color={Color.ColdGrey_800} textAlign={TextAlign.Left}>
            {faq.question}
          </Heading5>
          <Icon
            name={isExpanded ? 'chevron_up' : 'chevron_down'}
            variant={IconVariant.Inherit}
            aria-hidden="true"
          />
        </Box>
      </FaqRowButton>
      <Box
        id={contentId}
        role="region"
        display={isExpanded ? Display.Block : Display.None}
        hidden={!isExpanded}
        paddingV={2}
        paddingH={0.5}
      >
        <Body2 color={Color.ColdGrey_800}>{faq.answer}</Body2>
      </Box>
    </FaqItemDivider>
  );
}

type Props = {
  discountSteps: PricingDTOTypes.DiscountStepDTO[];
};

export function PricingFaq({ discountSteps }: Props): JSX.Element {
  const faqs = buildFaqs(discountSteps);

  return (
    <Box>
      <Heading2>Frequently asked questions</Heading2>
      <Space size={4} />
      <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
        {faqs.map((faq) => (
          <FaqRow key={faq.id} faq={faq} />
        ))}
      </Box>
    </Box>
  );
}
